// ============================================
// PAGE - Editar Producto
// ============================================

import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProductoVendibleById, useInventarioProducto, useMermaProducto, useUpdateProductoAlmacen, useUpdateInventario, useDeleteInventario, useUIStore, formatFechaCL, useCategoriasAlmacen, useAssignCategoriaAlmacen, useRemoveCategoriaFromProducto, useSession,  useGranelCategorias, useGranelTipos, useGranelProductosEstandar, useSetNombreProductoAlmacen, useDeleteNombreProductoAlmacen } from '@almacenes/core';
import type { GranelCategoria, GranelTipo, GranelProductoEstandar } from '@almacenes/core';
import { Button } from '@almacenes/ui';
import { MdArrowBack, MdSave, MdAdd, MdRemoveCircle, MdWarning, MdEdit, MdDelete, MdClose, MdQrCode2 } from 'react-icons/md';
import { FaCrown } from 'react-icons/fa6';
import productosPlaceholder from '@/assets/images/imagen_no_disponible.png';
import { AddStockModal } from '@/components/AddStockModal';

type Tab = 'datos' | 'inventario';

export const EditarProductoPage = () => {
  const { productoAlmacenId } = useParams<{ productoAlmacenId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('datos');
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [showMermaModal, setShowMermaModal] = useState(false);
  const [showConfirmSaveModal, setShowConfirmSaveModal] = useState(false);
  const [showEditInventarioModal, setShowEditInventarioModal] = useState(false);
  const [showDeleteInventarioModal, setShowDeleteInventarioModal] = useState(false);
  const [selectedInventarioItem, setSelectedInventarioItem] = useState<any>(null);
  const [mermaInput, setMermaInput] = useState('');
  
  // Estados para generación de código granel
  const [showGenerarCodigoModal, setShowGenerarCodigoModal] = useState(false);
  const [granelCategoriaSeleccionada, setGranelCategoriaSeleccionada] = useState<GranelCategoria | null>(null);
  const [granelTipoSeleccionado, setGranelTipoSeleccionado] = useState<GranelTipo | null>(null);
  const [granelMarcaSeleccionada, setGranelMarcaSeleccionada] = useState<GranelProductoEstandar | null>(null);
  const [codigoGranelGenerado, setCodigoGranelGenerado] = useState('');
  const [granelCategoriaBusqueda, setGranelCategoriaBusqueda] = useState('');
  const [granelTipoBusqueda, setGranelTipoBusqueda] = useState('');
  const [granelMarcaBusqueda, setGranelMarcaBusqueda] = useState('');
  const [mostrarDropdownCategoria, setMostrarDropdownCategoria] = useState(false);
  const [mostrarDropdownTipo, setMostrarDropdownTipo] = useState(false);
  const [mostrarDropdownMarca, setMostrarDropdownMarca] = useState(false);
  
  // Estados para editar inventario
  const [editStock, setEditStock] = useState('');
  const [editPrecioCompra, setEditPrecioCompra] = useState('');
  const [editPrecioVenta, setEditPrecioVenta] = useState('');
  const [editFechaVencimiento, setEditFechaVencimiento] = useState('');
  
  // Estados para campos editables del producto
  const [categoriaDropdownOpen, setCategoriaDropdownOpen] = useState(false);
  const [filtroCategorias, setFiltroCategorias] = useState('');
  const categoriaDropdownRef = useRef<HTMLDivElement>(null);
  
  // Referencias para dropdowns de granel
  const categoriaGranelDropdownRef = useRef<HTMLDivElement>(null);
  const tipoGranelDropdownRef = useRef<HTMLDivElement>(null);
  const marcaGranelDropdownRef = useRef<HTMLDivElement>(null);
  const [nombreProducto, setNombreProducto] = useState('');
  const [codigoProducto, setCodigoProducto] = useState('');
  const [aliasNombre, setAliasNombre] = useState('');
  const [aliasOriginal, setAliasOriginal] = useState('');
  const [venderPor, setVenderPor] = useState<'U' | 'F'>('U');
  const [precioCompra, setPrecioCompra] = useState('');
  const [precioVenta, setPrecioVenta] = useState('');
  
  const { data: session } = useSession();
  const hasPremiumAccess = session?.hasPremiumAccess || false;

  const { data: producto, isLoading } = useProductoVendibleById(Number(productoAlmacenId));
  const { data: inventario, isLoading: isLoadingInventario } = useInventarioProducto(
    Number(productoAlmacenId),
    activeTab === 'inventario'
  );
  const { data: categorias } = useCategoriasAlmacen();
  const asignarCategoria = useAssignCategoriaAlmacen();
  const quitarCategoria = useRemoveCategoriaFromProducto();
  const setAliasMutation = useSetNombreProductoAlmacen();
  const deleteAliasMutation = useDeleteNombreProductoAlmacen();
  const mermaMutation = useMermaProducto();
  const updateProductoMutation = useUpdateProductoAlmacen();
  const updateInventarioMutation = useUpdateInventario();
  const deleteInventarioMutation = useDeleteInventario();
  const addToast = useUIStore((state) => state.addToast);
  
  // Hooks de granel
  const { data: granelCategorias = [] } = useGranelCategorias(showGenerarCodigoModal);
  const { data: granelTipos = [] } = useGranelTipos(
    granelCategoriaSeleccionada?.id ?? null,
    showGenerarCodigoModal && !!granelCategoriaSeleccionada
  );
  // Las marcas salen de los productos estándar del tipo seleccionado (traen su código de barra)
  const { data: granelMarcas = [] } = useGranelProductosEstandar(
    granelTipoSeleccionado?.id ?? null,
    showGenerarCodigoModal && !!granelTipoSeleccionado
  );

  // Scroll al tope al montar el componente
  useEffect(() => {
    // Scroll del main layout
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTo(0, 0);
    }
    // Fallback por si acaso
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoriaDropdownRef.current && !categoriaDropdownRef.current.contains(e.target as Node)) {
        setCategoriaDropdownOpen(false);
        setFiltroCategorias('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Pre-llenar campos del producto cuando carga
  useEffect(() => {
    if (producto) {
      setNombreProducto(producto.productoNombre);
      setCodigoProducto(producto.codigo);
      setVenderPor(producto.venderPor);
      setPrecioCompra(producto.precioCompraActual?.toString() || '');
      setPrecioVenta(producto.precioVentaActual?.toString() || '');
      // El alias se guarda en productoNombre si existe (viene del COALESCE de la vista)
      setAliasNombre(producto.aliasNombre ?? '');
      setAliasOriginal(producto.aliasNombre ?? '');
    }
  }, [producto]);

  const handleVolver = () => {
    navigate('/inventario');
  };

  const handleGuardar = () => {
    setShowConfirmSaveModal(true);
  };
  
  // El código de barra viene listo desde el producto estándar seleccionado
  useEffect(() => {
    setCodigoGranelGenerado(granelMarcaSeleccionada?.codigo ?? '');
  }, [granelMarcaSeleccionada]);
  
  // Cerrar dropdowns al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        categoriaGranelDropdownRef.current &&
        !categoriaGranelDropdownRef.current.contains(e.target as Node)
      ) {
        setMostrarDropdownCategoria(false);
      }
      if (
        tipoGranelDropdownRef.current &&
        !tipoGranelDropdownRef.current.contains(e.target as Node)
      ) {
        setMostrarDropdownTipo(false);
      }
      if (
        marcaGranelDropdownRef.current &&
        !marcaGranelDropdownRef.current.contains(e.target as Node)
      ) {
        setMostrarDropdownMarca(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Filtros de granel
  const granelCategoriasFiltradas = granelCategorias.filter((cat) =>
    cat.nombre.toLowerCase().includes(granelCategoriaBusqueda.toLowerCase())
  );
  
  const granelTiposFiltrados = granelTipos.filter((tipo) =>
    tipo.nombre.toLowerCase().includes(granelTipoBusqueda.toLowerCase())
  );
  
  const granelMarcasFiltradas = granelMarcas.filter((marca) =>
    marca.marcaNombre.toLowerCase().includes(granelMarcaBusqueda.toLowerCase())
  );
  
  // Handlers de selección
  const handleGranelCategoriaSelect = (cat: GranelCategoria) => {
    setGranelCategoriaSeleccionada(cat);
    setGranelCategoriaBusqueda(cat.nombre);
    setMostrarDropdownCategoria(false);
    setGranelTipoSeleccionado(null);
    setGranelTipoBusqueda('');
    setGranelMarcaSeleccionada(null);
    setGranelMarcaBusqueda('');
  };
  
  const handleGranelTipoSelect = (tipo: GranelTipo) => {
    setGranelTipoSeleccionado(tipo);
    setGranelTipoBusqueda(tipo.nombre);
    setMostrarDropdownTipo(false);
    setGranelMarcaSeleccionada(null);
    setGranelMarcaBusqueda('');
  };
  
  const handleGranelMarcaSelect = (marca: GranelProductoEstandar) => {
    setGranelMarcaSeleccionada(marca);
    setGranelMarcaBusqueda(marca.marcaNombre);
    setMostrarDropdownMarca(false);
  };

  const handleGuardarAlias = async () => {
    const id = Number(productoAlmacenId);
    const trimmed = aliasNombre.trim();
    if (trimmed === aliasOriginal) return;

    try {
      if (trimmed === '') {
        await deleteAliasMutation.mutateAsync(id);
        setAliasOriginal('');
        addToast('success', 'Nombre propio eliminado');
      } else {
        await setAliasMutation.mutateAsync({ productoAlmacenId: id, nombre: trimmed });
        setAliasOriginal(trimmed);
        addToast('success', 'Nombre propio guardado');
      }
    } catch {
      addToast('error', 'Error al guardar el nombre propio');
    }
  };

  const handleCategoriaChange = async (value: string) => {
    const id = Number(productoAlmacenId);
    if (value === '') {
      quitarCategoria.mutate(id, {
        onSuccess: () => addToast('success', 'Categoría quitada'),
        onError: () => addToast('error', 'Error al quitar la categoría'),
      });
    } else {
      asignarCategoria.mutate({ productoAlmacenId: id, categoriaAlmacenId: Number(value) }, {
        onSuccess: () => addToast('success', 'Categoría asignada'),
        onError: () => addToast('error', 'Error al asignar la categoría'),
      });
    }
  };

  const handleConfirmSave = async () => {
    if (!producto) return;

    if (venderPor === 'U' && producto.venderPor === 'F' && !producto.esPack && !Number.isInteger(producto.stock)) {
      const stockActual = producto.stock.toLocaleString('es-CL', { maximumFractionDigits: 3 });
      addToast('error', `No se puede cambiar a Unidad: el stock actual (${stockActual}) tiene decimales. Ajústalo a un entero antes de cambiar.`);
      setShowConfirmSaveModal(false);
      return;
    }

    const updateDto: any = {};

    // Solo incluir campos que no estén disabled
    if (!producto.esRetail) {
      if (nombreProducto !== producto.productoNombre) {
        updateDto.nombreProducto = nombreProducto;
      }
      if (codigoProducto !== producto.codigo) {
        updateDto.codigoProducto = codigoProducto;
      }
    }

    // Precios (siempre editables)
    const precioCompraNum = parseFloat(precioCompra);
    const precioVentaNum = parseFloat(precioVenta);

    if (!isNaN(precioCompraNum) && precioCompraNum !== producto.precioCompraActual) {
      updateDto.precioCompraActual = precioCompraNum;
    }
    if (!isNaN(precioVentaNum) && precioVentaNum !== producto.precioVentaActual) {
      updateDto.precioVentaActual = precioVentaNum;
    }

    // Vender por (siempre editable)
    if (venderPor !== producto.venderPor) {
      updateDto.venderPor = venderPor;
    }

    // Agregar IDs de granel si se generó código
    if (codigoGranelGenerado && granelMarcaSeleccionada) {
      updateDto.venderPor = 'F';
      updateDto.granelProductoEstandarId = granelMarcaSeleccionada.id;
    }

    // Si no hay cambios, no hacer nada
    if (Object.keys(updateDto).length === 0) {
      addToast('info', 'No hay cambios para guardar');
      setShowConfirmSaveModal(false);
      return;
    }

    try {
      await updateProductoMutation.mutateAsync({
        productoAlmacenId: Number(productoAlmacenId),
        dto: updateDto,
      });

      addToast('success', 'Producto actualizado exitosamente');
      setShowConfirmSaveModal(false);
      
      // Resetear estados de granel
      setCodigoGranelGenerado('');
      setGranelCategoriaSeleccionada(null);
      setGranelTipoSeleccionado(null);
      setGranelMarcaSeleccionada(null);
    } catch (error: any) {
      console.error('Error al actualizar producto:', error);
      const errorMessage = error?.message || 'Error al actualizar el producto. Por favor, intenta nuevamente.';
      addToast('error', errorMessage);
    }
  };

  const handleMerma = async () => {
    if (!producto || !mermaInput) {
      addToast('warning', 'Por favor ingresa la cantidad de merma');
      return;
    }

    // Normalizar valor (convertir coma a punto)
    const cantidad = parseFloat(normalizeNumberInput(mermaInput));
    
    if (isNaN(cantidad) || cantidad <= 0) {
      addToast('error', 'La cantidad debe ser un número positivo mayor a cero');
      return;
    }

    // Validar entero si es venta por unidad
    if (producto.venderPor === 'U' && !Number.isInteger(cantidad)) {
      addToast('error', 'La cantidad debe ser un número entero para productos vendidos por unidad');
      return;
    }

    try {
      await mermaMutation.mutateAsync({
        productoAlmacenId: Number(productoAlmacenId),
        cantidad,
      });

      addToast('success', 'Merma registrada exitosamente');
      setShowMermaModal(false);
      setMermaInput('');
    } catch (error: any) {
      console.error('Error al registrar merma:', error);
      
      // Intentar obtener el mensaje del servidor
      const errorMessage = error?.message || 'Error al registrar merma. Por favor, intenta nuevamente.';
      
      addToast('error', errorMessage);
    }
  };

  const handleCancelMerma = () => {
    setShowMermaModal(false);
    setMermaInput('');
  };

  // Funciones para editar registro de inventario
  const handleOpenEditInventario = (item: any) => {
    setSelectedInventarioItem(item);
    setEditStock(item.stock.toString());
    setEditPrecioCompra(item.precioCompra.toString());
    setEditPrecioVenta(item.precioVenta.toString());
    setEditFechaVencimiento(item.fechaVencimiento ? item.fechaVencimiento.split('T')[0] : '');
    setShowEditInventarioModal(true);
  };

  const handleConfirmEditInventario = async () => {
    if (!selectedInventarioItem || !producto) return;

    // Validar campos requeridos
    const stockNum = parseFloat(editStock);
    const precioCompraNum = parseFloat(editPrecioCompra);
    const precioVentaNum = parseFloat(editPrecioVenta);

    if (isNaN(stockNum) || stockNum < 0) {
      addToast('error', 'El stock debe ser un número válido');
      return;
    }

    if (isNaN(precioCompraNum) || precioCompraNum < 0) {
      addToast('error', 'El precio de compra debe ser un número válido');
      return;
    }

    if (isNaN(precioVentaNum) || precioVentaNum < 0) {
      addToast('error', 'El precio de venta debe ser un número válido');
      return;
    }

    // Validar entero si es venta por unidad
    if (producto.venderPor === 'U' && !Number.isInteger(stockNum)) {
      addToast('error', 'El stock debe ser un número entero para productos vendidos por unidad');
      return;
    }

    try {
      await updateInventarioMutation.mutateAsync({
        inventarioId: selectedInventarioItem.id,
        dto: {
          stock: stockNum,
          precioCompra: precioCompraNum,
          precioVenta: precioVentaNum,
          fechaVencimiento: editFechaVencimiento || null,
        },
      });

      addToast('success', 'Registro de inventario actualizado exitosamente');
      setShowEditInventarioModal(false);
      setSelectedInventarioItem(null);
    } catch (error: any) {
      console.error('Error al actualizar inventario:', error);
      const errorMessage = error?.message || 'Error al actualizar el registro. Por favor, intenta nuevamente.';
      addToast('error', errorMessage);
    }
  };

  // Funciones para eliminar registro de inventario
  const handleOpenDeleteInventario = (item: any) => {
    setSelectedInventarioItem(item);
    setShowDeleteInventarioModal(true);
  };

  const handleConfirmDeleteInventario = async () => {
    if (!selectedInventarioItem || !producto) return;

    try {
      await deleteInventarioMutation.mutateAsync({
        inventarioId: selectedInventarioItem.id,
        productoAlmacenId: producto.productoAlmacenId,
      });

      addToast('success', 'Registro de inventario eliminado exitosamente');
      setShowDeleteInventarioModal(false);
      setSelectedInventarioItem(null);
    } catch (error: any) {
      console.error('Error al eliminar inventario:', error);
      const errorMessage = error?.message || 'Error al eliminar el registro. Por favor, intenta nuevamente.';
      addToast('error', errorMessage);
    }
  };

  // Normalizar entrada de números: permitir coma y punto, convertir internamente a punto
  const normalizeNumberInput = (value: string): string => {
    return value.replace(',', '.');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-solid border-current border-r-transparent mx-auto" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
          <p className="mt-4 text-gray-600 text-lg">Cargando producto...</p>
        </div>
      </div>
    );
  }

  if (!producto) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#11194F' }}>
            Producto no encontrado
          </h2>
          <Button onClick={handleVolver}>
            Volver al Inventario
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 md:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={handleVolver}
              className="p-2 rounded-lg transition-all hover:bg-gray-200"
              style={{ color: '#11194F' }}
            >
              <MdArrowBack className="text-2xl" />
            </button>
            <h1 className="text-4xl font-bold" style={{ color: '#11194F' }}>
              Editar Producto
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowMermaModal(true)}
              className="px-6 py-3 rounded-xl font-semibold text-white shadow-md hover:shadow-lg transition-all hover:opacity-90 flex items-center gap-2"
              style={{ backgroundColor: '#EF4444' }}
            >
              <MdRemoveCircle className="text-xl" />
              <span>Merma</span>
            </button>

            <Button
              variant="primary"
              size="lg"
              onClick={handleGuardar}
              className="shadow-md hover:shadow-lg"
            >
              <div className="flex items-center gap-2">
                <MdSave className="text-xl" />
                <span>Guardar Cambios</span>
              </div>
            </Button>
          </div>
        </div>

        {/* Formulario */}
        <div className="bg-white rounded-xl shadow-lg">
          {/* Pestañas */}
          <div className="flex border-b" style={{ borderColor: '#E5E7EB' }}>
            <button
              onClick={() => setActiveTab('datos')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                activeTab === 'datos'
                  ? 'border-b-2'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={
                activeTab === 'datos'
                  ? { borderColor: '#FACC15', backgroundColor: '#FFFBEB', color: '#11194F' }
                  : {}
              }
            >
              Datos del Producto
            </button>
            <button
              onClick={() => setActiveTab('inventario')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors ${
                activeTab === 'inventario'
                  ? 'border-b-2'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={
                activeTab === 'inventario'
                  ? { borderColor: '#FACC15', backgroundColor: '#FFFBEB', color: '#11194F' }
                  : {}
              }
            >
              Inventario
            </button>
          </div>

          {/* Contenido de las pestañas */}
          <div className="p-8">
            {activeTab === 'datos' ? (
              <>
                {/* Imagen del producto */}
                <div className="mb-8 flex justify-center">
                  <div className="relative">
                    {producto.imagenUrl ? (
                      <img
                        src={producto.imagenUrl}
                        alt={producto.productoNombre}
                        className="w-48 h-48 object-cover rounded-lg border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-48 h-48 bg-gray-100 rounded-lg border-2 border-gray-200 flex items-center justify-center p-4">
                        <img 
                          src={productosPlaceholder} 
                          alt="Producto" 
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Información básica */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
                      Nombre del Producto
                    </label>
                    <input
                      type="text"
                      value={nombreProducto}
                      onChange={(e) => setNombreProducto(e.target.value)}
                      disabled={producto.esRetail}
                      className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors ${
                        producto.esRetail ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                      style={{ borderColor: '#E5E7EB' }}
                    />
                    {producto.esRetail && (
                      <p className="text-xs text-gray-500 mt-1">Este campo no puede editarse en productos retail</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
                      Código
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={codigoProducto}
                        onChange={(e) => setCodigoProducto(e.target.value)}
                        disabled={producto.esRetail}
                        className={`flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors ${
                          producto.esRetail ? 'bg-gray-50 cursor-not-allowed' : ''
                        }`}
                        style={{ borderColor: '#E5E7EB' }}
                      />
                      
                      {!producto.esRetail && venderPor === 'F' && (
                        <button
                          type="button"
                          onClick={() => setShowGenerarCodigoModal(true)}
                          className="px-4 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90 flex items-center gap-2"
                          style={{ backgroundColor: '#43BFFF' }}
                          title="Generar código de barra granel"
                        >
                          <MdQrCode2 className="text-xl" />
                          Generar Código
                        </button>
                      )}
                      
                    </div>
                    {producto.esRetail && (
                      <p className="text-xs text-gray-500 mt-1">Este campo no puede editarse en productos retail</p>
                    )}
                    {codigoGranelGenerado && (
                      <div className="mt-2 p-3 rounded-lg" style={{ backgroundColor: '#DBEAFE' }}>
                        
                        <p className="text-xs text-gray-600 mt-1">
                          Este código se asignará al guardar los cambios
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Formato de venta */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
                      Vender Por
                    </label>
                    <select
                      value={venderPor}
                      onChange={(e) => setVenderPor(e.target.value as 'U' | 'F')}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors"
                      style={{ borderColor: '#E5E7EB' }}
                    >
                      <option value="U">Unidad</option>
                      <option value="F">Granel</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
                      Stock Actual
                    </label>
                    <input
                      type="text"
                      value={producto.esPack ? '-' : producto.stock.toFixed(producto.venderPor === 'F' ? 3 : 0)}
                      disabled
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                  </div>
                </div>

                {/* Precios */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
                      Precio de Compra
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                        $
                      </span>
                      <input
                        type="number"
                        value={precioCompra}
                        onChange={(e) => setPrecioCompra(e.target.value)}
                        className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors"
                        style={{ borderColor: '#E5E7EB' }}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
                      Precio de Venta
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                        $
                      </span>
                      <input
                        type="number"
                        value={precioVenta}
                        onChange={(e) => setPrecioVenta(e.target.value)}
                        className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors"
                        style={{ borderColor: '#E5E7EB' }}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Categoría propia */}
                <div className="mt-6">
                  <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
                    <span className="flex items-center gap-2">
                      Categoría del negocio
                      {!hasPremiumAccess && <FaCrown className="text-purple-500 text-sm" title="Función Premium" />}
                    </span>
                  </label>
                  {hasPremiumAccess ? (
                    <div className="relative" ref={categoriaDropdownRef}>
                      {/* Trigger — se ve como un select normal */}
                      <button
                        type="button"
                        onClick={() => { setCategoriaDropdownOpen(v => !v); setFiltroCategorias(''); }}
                        disabled={asignarCategoria.isPending || quitarCategoria.isPending}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-left flex items-center justify-between transition-colors hover:border-gray-400 disabled:opacity-50 bg-white"
                        style={{ borderColor: categoriaDropdownOpen ? '#43BFFF' : '#E5E7EB' }}
                      >
                        <span className={producto.categoriaAlmacen ? '' : 'text-gray-400'}>
                          {producto.categoriaAlmacen?.nombre ?? 'Sin categoría'}
                        </span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${categoriaDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {/* Dropdown */}
                      {categoriaDropdownOpen && (
                        <div className="absolute z-20 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden">
                          <div className="p-2 border-b border-gray-100">
                            <input
                              type="text"
                              autoFocus
                              value={filtroCategorias}
                              onChange={(e) => setFiltroCategorias(e.target.value)}
                              placeholder="Buscar categoría..."
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-300"
                            />
                          </div>
                          <ul className="max-h-48 overflow-y-auto">
                            <li>
                              <button
                                type="button"
                                onClick={() => { handleCategoriaChange(''); setCategoriaDropdownOpen(false); setFiltroCategorias(''); }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${!producto.categoriaAlmacen ? 'bg-blue-50 font-semibold' : 'text-gray-400'}`}
                              >
                                Sin categoría
                              </button>
                            </li>
                            {categorias
                              ?.filter(c => c.nombre.toLowerCase().includes(filtroCategorias.toLowerCase()))
                              .map((cat) => (
                                <li key={cat.id}>
                                  <button
                                    type="button"
                                    onClick={() => { handleCategoriaChange(cat.id.toString()); setCategoriaDropdownOpen(false); setFiltroCategorias(''); }}
                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${producto.categoriaAlmacen?.id === cat.id ? 'bg-blue-50 font-semibold' : ''}`}
                                    style={producto.categoriaAlmacen?.id === cat.id ? { color: '#43BFFF' } : {}}
                                  >
                                    {cat.nombre}
                                  </button>
                                </li>
                              ))}
                            {categorias?.filter(c => c.nombre.toLowerCase().includes(filtroCategorias.toLowerCase())).length === 0 && (
                              <li className="px-4 py-3 text-sm text-gray-400 text-center">Sin resultados</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50">
                      <FaCrown className="text-purple-500" />
                      <span className="text-sm text-gray-500">Disponible con suscripción Premium</span>
                    </div>
                  )}
                </div>

                {/* Nombre propio del almacén */}
                <div className="mt-6">
                  <label className="block text-sm font-bold mb-1" style={{ color: '#11194F' }}>
                    Nombre propio en mi almacén
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Si lo defines, este nombre reemplazará al nombre global solo en tu almacén.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={aliasNombre}
                      onChange={(e) => setAliasNombre(e.target.value)}
                      placeholder={producto.productoNombre}
                      maxLength={255}
                      className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none transition-colors"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                    <button
                      type="button"
                      onClick={handleGuardarAlias}
                      disabled={
                        aliasNombre.trim() === aliasOriginal ||
                        setAliasMutation.isPending ||
                        deleteAliasMutation.isPending
                      }
                      className="px-5 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
                      style={{ backgroundColor: '#43BFFF' }}
                    >
                      {setAliasMutation.isPending || deleteAliasMutation.isPending ? 'Guardando...' : 'Guardar'}
                    </button>
                  </div>
                  {aliasOriginal && (
                    <p className="text-xs mt-1" style={{ color: '#43BFFF' }}>
                      Nombre propio activo. Deja el campo vacío y guarda para eliminar.
                    </p>
                  )}
                </div>

                {/* IDs informativos */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">Información del Sistema</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">ID Producto: </span>
                      <span className="font-semibold" style={{ color: '#11194F' }}>{producto.productoId}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">ID Almacén: </span>
                      <span className="font-semibold" style={{ color: '#11194F' }}>{producto.productoAlmacenId}</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Vista de Inventario */}
                <div className="mb-4 flex justify-end">
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => setShowAddStockModal(true)}
                    className="shadow-md hover:shadow-lg"
                  >
                    <div className="flex items-center gap-2">
                      <MdAdd className="text-xl" />
                      <span>Añadir Stock</span>
                    </div>
                  </Button>
                </div>

                {isLoadingInventario ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent" style={{ borderTopColor: '#43BFFF', borderBottomColor: '#43BFFF', borderLeftColor: '#43BFFF' }} />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead style={{ backgroundColor: '#F3F4F9' }}>
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                            Fecha Registro
                          </th>
                          <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                            Stock
                          </th>
                          <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                            Precio Compra
                          </th>
                          <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                            Precio Venta
                          </th>
                          <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                            Fecha Vencimiento
                          </th>
                          <th className="px-6 py-4 text-center text-sm font-bold" style={{ color: '#11194F' }}>
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {!inventario || inventario.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-6 py-12 text-center text-gray-600">
                              No hay registros de inventario
                            </td>
                          </tr>
                        ) : (
                          inventario.map((item, index) => (
                            <tr
                              key={item.id}
                              className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                            >
                              <td className="px-6 py-4">
                                <span className="font-medium" style={{ color: '#11194F' }}>
                                  {formatFechaCL(item.fechaRegistro)}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span
                                  className={`font-semibold ${
                                    item.stock < 0
                                      ? 'text-red-600'
                                      : item.stock === 0
                                      ? 'text-orange-600'
                                      : 'text-green-600'
                                  }`}
                                >
                                  {item.stock.toFixed(producto.venderPor === 'F' ? 3 : 0)}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="font-semibold" style={{ color: '#11194F' }}>
                                  ${item.precioCompra.toLocaleString('es-CL')}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="font-semibold" style={{ color: '#11194F' }}>
                                  ${item.precioVenta.toLocaleString('es-CL')}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-gray-600">
                                  {item.fechaVencimiento 
                                    ? new Date(item.fechaVencimiento + 'T00:00:00').toLocaleDateString('es-CL')
                                    : '-'
                                  }
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => handleOpenEditInventario(item)}
                                    className="p-2 rounded-lg transition-all hover:bg-blue-100 text-blue-600"
                                    title="Editar registro"
                                  >
                                    <MdEdit className="text-xl" />
                                  </button>
                                  <button
                                    onClick={() => handleOpenDeleteInventario(item)}
                                    className="p-2 rounded-lg transition-all hover:bg-red-100 text-red-600"
                                    title="Eliminar registro"
                                  >
                                    <MdDelete className="text-xl" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Añadir Stock */}
      {producto && (
        <AddStockModal
          isOpen={showAddStockModal}
          onClose={() => setShowAddStockModal(false)}
          producto={producto}
        />
      )}

      {/* Modal de Merma */}
      {showMermaModal && producto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full" style={{ backgroundColor: '#FEE2E2' }}>
              <MdRemoveCircle className="text-4xl text-red-500" />
            </div>
            
            <h2 className="text-2xl font-bold text-center mb-2" style={{ color: '#11194F' }}>
              Registrar Merma
            </h2>
            
            <p className="text-center text-gray-600 mb-6">
              {producto.productoNombre}
            </p>

            {/* Información de stock actual */}
            <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#F3F4F9' }}>
              <p className="text-sm text-gray-600">
                Stock actual: <span className="font-bold" style={{ color: '#11194F' }}>
                  {producto.stock.toFixed(producto.venderPor === 'F' ? 3 : 0)}
                </span>
              </p>
            </div>

            {/* Formulario */}
            <div className="mb-6">
              <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
                Cantidad de Merma <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={mermaInput}
                onChange={(e) => {
                  const value = e.target.value;
                  // Permitir solo números, punto y coma
                  if (/^[0-9]*[.,]?[0-9]*$/.test(value)) {
                    setMermaInput(value);
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors"
                style={{ borderColor: '#E5E7EB' }}
                placeholder={producto.venderPor === 'U' ? 'Ej: 5' : 'Ej: 2,500'}
              />
              <p className="text-xs text-gray-500 mt-1">
                {producto.venderPor === 'U' ? 'Solo números enteros' : 'Use coma (,) para decimales'}
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleCancelMerma}
                disabled={mermaMutation.isPending}
                className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all hover:bg-gray-100 border-2 border-gray-300 disabled:opacity-50"
                style={{ color: '#11194F' }}
              >
                Cancelar
              </button>
              
              <button
                onClick={handleMerma}
                disabled={mermaMutation.isPending}
                className="flex-1 px-6 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 bg-red-500"
              >
                {mermaMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    <span>Registrando...</span>
                  </>
                ) : (
                  'Registrar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación Guardar */}
      {showConfirmSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full" style={{ backgroundColor: '#FEF3C7' }}>
              <MdWarning className="text-4xl" style={{ color: '#F59E0B' }} />
            </div>
            
            <h2 className="text-2xl font-bold text-center mb-2" style={{ color: '#11194F' }}>
              Confirmar Cambios
            </h2>
            
            <p className="text-center text-gray-600 mb-6">
              ¿Estás seguro de que deseas actualizar los datos del producto?
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmSaveModal(false)}
                disabled={updateProductoMutation.isPending}
                className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all hover:bg-gray-100 border-2 border-gray-300 disabled:opacity-50"
                style={{ color: '#11194F' }}
              >
                Cancelar
              </button>
              
              <button
                onClick={handleConfirmSave}
                disabled={updateProductoMutation.isPending}
                className="flex-1 px-6 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#43BFFF' }}
              >
                {updateProductoMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    <span>Guardando...</span>
                  </>
                ) : (
                  'Sí, Guardar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Editar Inventario */}
      {showEditInventarioModal && selectedInventarioItem && producto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-fadeIn">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full" style={{ backgroundColor: '#DBEAFE' }}>
              <MdEdit className="text-4xl text-blue-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-center mb-2" style={{ color: '#11194F' }}>
              Editar Registro de Inventario
            </h2>
            
            <p className="text-center text-gray-600 mb-6">
              {producto.productoNombre}
            </p>

            {/* Formulario de edición */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
                  Stock <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={editStock}
                  onChange={(e) => setEditStock(e.target.value)}
                  step={producto.venderPor === 'F' ? '0.001' : '1'}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors"
                  style={{ borderColor: '#E5E7EB' }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
                    Precio Compra <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                      $
                    </span>
                    <input
                      type="number"
                      value={editPrecioCompra}
                      onChange={(e) => setEditPrecioCompra(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
                    Precio Venta <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                      $
                    </span>
                    <input
                      type="number"
                      value={editPrecioVenta}
                      onChange={(e) => setEditPrecioVenta(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors"
                      style={{ borderColor: '#E5E7EB' }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2" style={{ color: '#11194F' }}>
                  Fecha Vencimiento
                </label>
                <input
                  type="date"
                  value={editFechaVencimiento}
                  onChange={(e) => setEditFechaVencimiento(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary transition-colors"
                  style={{ borderColor: '#E5E7EB' }}
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowEditInventarioModal(false)}
                disabled={updateInventarioMutation.isPending}
                className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all hover:bg-gray-100 border-2 border-gray-300 disabled:opacity-50"
                style={{ color: '#11194F' }}
              >
                Cancelar
              </button>
              
              <button
                onClick={handleConfirmEditInventario}
                disabled={updateInventarioMutation.isPending}
                className="flex-1 px-6 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#43BFFF' }}
              >
                {updateInventarioMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    <span>Guardando...</span>
                  </>
                ) : (
                  'Guardar Cambios'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Eliminar Inventario */}
      {showDeleteInventarioModal && selectedInventarioItem && producto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fadeIn">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full" style={{ backgroundColor: '#FEE2E2' }}>
              <MdDelete className="text-4xl text-red-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-center mb-2" style={{ color: '#11194F' }}>
              Eliminar Registro
            </h2>
            
            <p className="text-center text-gray-600 mb-4">
              ¿Estás seguro de que deseas eliminar este registro de inventario?
            </p>

            {/* Información del registro */}
            <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: '#F3F4F9' }}>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Fecha:</span>
                  <span className="font-semibold" style={{ color: '#11194F' }}>
                    {formatFechaCL(selectedInventarioItem.fechaRegistro)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Stock:</span>
                  <span className="font-semibold" style={{ color: '#11194F' }}>
                    {selectedInventarioItem.stock.toFixed(producto.venderPor === 'F' ? 3 : 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Precio Compra:</span>
                  <span className="font-semibold" style={{ color: '#11194F' }}>
                    ${selectedInventarioItem.precioCompra.toLocaleString('es-CL')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Precio Venta:</span>
                  <span className="font-semibold" style={{ color: '#11194F' }}>
                    ${selectedInventarioItem.precioVenta.toLocaleString('es-CL')}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-center text-sm text-red-600 font-semibold mb-6">
              Esta acción no se puede deshacer
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteInventarioModal(false)}
                disabled={deleteInventarioMutation.isPending}
                className="flex-1 px-6 py-3 rounded-xl font-semibold transition-all hover:bg-gray-100 border-2 border-gray-300 disabled:opacity-50"
                style={{ color: '#11194F' }}
              >
                Cancelar
              </button>
              
              <button
                onClick={handleConfirmDeleteInventario}
                disabled={deleteInventarioMutation.isPending}
                className="flex-1 px-6 py-3 rounded-xl font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 bg-red-600"
              >
                {deleteInventarioMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    <span>Eliminando...</span>
                  </>
                ) : (
                  'Sí, Eliminar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Generar Código Granel */}
      {showGenerarCodigoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full min-h-[570px] max-h-[95vh] flex flex-col">
            {/* Header del modal */}
            <div className="bg-white border-b-2 border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
                Generar Código Granel
              </h2>
              <button
                onClick={() => {
                  setShowGenerarCodigoModal(false);
                  setGranelCategoriaBusqueda('');
                  setGranelTipoBusqueda('');
                  setGranelMarcaBusqueda('');
                  setMostrarDropdownCategoria(false);
                  setMostrarDropdownTipo(false);
                  setMostrarDropdownMarca(false);
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <MdClose className="text-2xl" style={{ color: '#11194F' }} />
              </button>
            </div>

            {/* Contenido del modal */}
            <div className="p-6 overflow-y-auto flex-1 flex flex-col">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex-shrink-0">
                <p className="text-sm text-blue-900">
                  <span className="font-semibold">💡</span> Completa los 3 campos para generar un código EAN-13 válido.
                </p>
              </div>

              {/* Línea con los 3 campos */}
              <div className="flex flex-wrap lg:flex-nowrap items-start gap-3 mb-4 flex-shrink-0">
                {/* Categoría */}
                <div className="relative flex-1 min-w-[140px]" ref={categoriaGranelDropdownRef}>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
                    1. Categoría <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={granelCategoriaBusqueda}
                    onChange={(e) => {
                      setGranelCategoriaBusqueda(e.target.value);
                      setMostrarDropdownCategoria(true);
                    }}
                    onFocus={() => setMostrarDropdownCategoria(true)}
                    placeholder="Buscar..."
                    className="w-full px-3 py-2 text-sm border-2 rounded-lg focus:outline-none focus:border-blue-500 transition-colors bg-white"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                  {mostrarDropdownCategoria && (
                    <div className="absolute z-10 w-full mt-1 bg-white border-2 border-blue-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {granelCategoriasFiltradas.length > 0 ? (
                        granelCategoriasFiltradas.map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => handleGranelCategoriaSelect(cat)}
                            className="w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                          >
                            <div className="font-medium text-sm text-gray-900">{cat.nombre}</div>
                            {cat.descripcion && <div className="text-xs text-gray-500">{cat.descripcion}</div>}
                          </button>
                        ))
                      ) : (
                        <div className="p-3 text-sm text-gray-500">Sin resultados</div>
                      )}
                    </div>
                  )}
                </div>

                <div className="hidden lg:flex items-center pt-7 text-gray-400">→</div>

                {/* Tipo */}
                <div className="relative flex-1 min-w-[140px]" ref={tipoGranelDropdownRef}>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
                    2. Tipo <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={granelTipoBusqueda}
                    onChange={(e) => {
                      setGranelTipoBusqueda(e.target.value);
                      setMostrarDropdownTipo(true);
                    }}
                    onFocus={() => setMostrarDropdownTipo(true)}
                    placeholder="Buscar..."
                    disabled={!granelCategoriaSeleccionada}
                    className="w-full px-3 py-2 text-sm border-2 rounded-lg focus:outline-none focus:border-blue-500 transition-colors bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                  {mostrarDropdownTipo && (
                    <div className="absolute z-10 w-full mt-1 bg-white border-2 border-blue-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {granelTiposFiltrados.length > 0 ? (
                        granelTiposFiltrados.map((tipo) => (
                          <button
                            key={tipo.id}
                            onClick={() => handleGranelTipoSelect(tipo)}
                            className="w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                          >
                            <div className="font-medium text-sm text-gray-900">{tipo.nombre}</div>
                          </button>
                        ))
                      ) : (
                        <div className="p-3 text-sm text-gray-500">Sin resultados</div>
                      )}
                    </div>
                  )}
                </div>

                <div className="hidden lg:flex items-center pt-7 text-gray-400">→</div>

                {/* Marca */}
                <div className="relative flex-1 min-w-[140px]" ref={marcaGranelDropdownRef}>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
                    3. Marca <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={granelMarcaBusqueda}
                    onChange={(e) => {
                      setGranelMarcaBusqueda(e.target.value);
                      setMostrarDropdownMarca(true);
                    }}
                    onFocus={() => setMostrarDropdownMarca(true)}
                    placeholder="Buscar..."
                    disabled={!granelTipoSeleccionado}
                    className="w-full px-3 py-2 text-sm border-2 rounded-lg focus:outline-none focus:border-blue-500 transition-colors bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                  {mostrarDropdownMarca && (
                    <div className="absolute z-10 w-full mt-1 bg-white border-2 border-blue-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {granelMarcasFiltradas.length > 0 ? (
                        granelMarcasFiltradas.map((marca) => (
                          <button
                            key={marca.id}
                            onClick={() => handleGranelMarcaSelect(marca)}
                            className="w-full px-3 py-2 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                          >
                            <div className="font-medium text-sm text-gray-900">{marca.marcaNombre}</div>
                          </button>
                        ))
                      ) : (
                        <div className="p-3 text-sm text-gray-500">Sin resultados</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Código generado (centrado en el espacio restante) */}
              <div className="flex-1 flex flex-col justify-center min-h-0">
                {codigoGranelGenerado ? (
                  <div className="mt-3">
                    <div className="flex items-center justify-center gap-2 text-gray-500 mb-2">
                      <div className="flex-1 h-px bg-gray-300"></div>
                      <span className="text-xs font-semibold">Resultado</span>
                      <div className="flex-1 h-px bg-gray-300"></div>
                    </div>
                    <div className="flex items-center justify-center gap-3 px-4 py-3 bg-blue-50 border-2 border-blue-300 rounded-xl">
                      <div className="text-center">
                        <p className="text-xs font-semibold text-gray-600 mb-1">
                          Código EAN-13 generado
                        </p>
                        <p className="text-2xl font-bold text-blue-600 font-mono tracking-wider">
                          {codigoGranelGenerado}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Dígito verificador: <span className="font-semibold">{codigoGranelGenerado.slice(-1)}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl text-center">
                    <p className="text-sm text-gray-400 italic">
                      Selecciona categoría, tipo y marca para generar el código EAN-13
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Botones (siempre visibles al pie del modal) */}
            <div className="flex gap-3 px-6 py-4 border-t-2 border-gray-200 flex-shrink-0">
              <button
                onClick={() => {
                  setShowGenerarCodigoModal(false);
                  setGranelCategoriaBusqueda('');
                  setGranelTipoBusqueda('');
                  setGranelMarcaBusqueda('');
                  setMostrarDropdownCategoria(false);
                  setMostrarDropdownTipo(false);
                  setMostrarDropdownMarca(false);
                }}
                className="flex-1 px-6 py-3 text-lg font-semibold rounded-xl border-2 transition-all"
                style={{ borderColor: '#E5E7EB', color: '#11194F' }}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (codigoGranelGenerado) {
                    setCodigoProducto(codigoGranelGenerado);
                    setShowGenerarCodigoModal(false);
                    setGranelCategoriaBusqueda('');
                    setGranelTipoBusqueda('');
                    setGranelMarcaBusqueda('');
                    setMostrarDropdownCategoria(false);
                    setMostrarDropdownTipo(false);
                    setMostrarDropdownMarca(false);
                    addToast('success', 'Código generado exitosamente');
                  } else {
                    addToast('warning', 'Completa todos los campos para generar el código');
                  }
                }}
                disabled={!codigoGranelGenerado}
                className="flex-1 px-6 py-3 text-lg font-semibold text-white rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#43BFFF' }}
              >
                Aplicar Código
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
